import {
  type ApiClientOptions,
  type CursorPagedResponse,
  ApiClient,
} from '@/infra/api-client'
import { toURLSearchParams } from '@/utils/url-util'
import { connectToSSE } from '@/utils/sse'
import {
  delay,
  exhaustMap,
  filter,
  map,
  repeat,
  retry,
  share,
  take,
} from 'rxjs/operators'
import type {
  ConversationResource,
  ListMessagesRequest,
  MessageEventResource,
  MessageResource,
  OnlineStatusResource,
  ParticipantResource,
  SendTextMessageRequest,
  SendNoteRequest,
  UnreadMessageCountItemResource,
  UpdateParticipantRequest,
  MarkReadRequest,
  ListUserStatusRequest,
} from './resources/messages'
import type {
  RoomResource,
  RoomConnectionDescriptorResource,
} from './resources/rooms'
import { isOnline$ } from '@/utils/browser-util'
import { filterNonNil } from '@/utils/rx-util'
import type { Observable } from 'rxjs'

/**
 * Messages API root.
 */
export class MessageApi {
  readonly events$: Observable<MessageEventResource>

  readonly conversations
  readonly messages
  readonly participants
  readonly users
  readonly rooms

  constructor(private readonly _options: ApiClientOptions) {
    this.conversations = new ConversationApi(this._options)
    this.messages = new MessagesApi(this._options)
    this.participants = new ParticipantApi(this._options)
    this.users = new UserStatusApi(this._options)
    this.rooms = new RoomsApi(this._options)

    this.events$ = this._options.token$.pipe(
      take(1),
      exhaustMap((token) =>
        connectToSSE(`${this._options.baseURL}/?__authorization=${token}`),
      ),
      map((message) => {
        try {
          if (typeof message.data !== 'string') {
            console.error('Invalid event data', message)
            return null
          }

          return JSON.parse(message.data) as MessageEventResource
        } catch (err) {
          console.error('Failed to parse event data', err)
          return null
        }
      }),
      filterNonNil(),
      repeat(),
      retry({
        // Only retry while we're online.
        delay: () => isOnline$.pipe(filter(Boolean), delay(2_000)),
        resetOnSuccess: true,
      }),
      share(),
    )
  }
}

/**
 * API for conversations.
 */
export class ConversationApi extends ApiClient {
  /**
   * Gets a conversation by ID.
   */
  get(id: string, signal?: AbortSignal): Promise<ConversationResource> {
    return this._get(`v3/conversations/${id}`, undefined, { signal })
  }

  /**
   * Gets unread message counts for all conversations.
   */
  getUnreadCounts(
    signal?: AbortSignal,
  ): Promise<UnreadMessageCountItemResource[]> {
    return this._get(`v3/messages/unread`, undefined, { signal })
  }

  /**
   * Checks if users are online.
   */
  isOnline(userPublicIds: string[]): Promise<OnlineStatusResource[]> {
    return this._post(`v3/users/status/id-query`, userPublicIds)
  }
}

/**
 * API for messages.
 */
export class MessagesApi extends ApiClient {
  /**
   * Lists messages for a conversation.
   */
  list(
    request: ListMessagesRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<MessageResource>> {
    const params = toURLSearchParams({ ...request })
    return this._get(
      `v3/conversations/${request.conversation_id}/messages`,
      params,
      { signal },
    )
  }

  /**
   * Sends a text message to a conversation.
   */
  sendText(
    conversationId: string,
    request: SendTextMessageRequest,
  ): Promise<MessageResource> {
    return this._post(
      `v3/conversations/${conversationId}/messages/text`,
      request,
    )
  }

  /**
   * Sends a note to a conversation.
   */
  sendNote(
    conversationId: string,
    request: SendNoteRequest,
  ): Promise<MessageResource> {
    return this._post(
      `v3/conversations/${conversationId}/messages/note`,
      request,
    )
  }

  /**
   * Marks messages as read.
   */
  markRead(request: MarkReadRequest): Promise<void> {
    return this._post(`v3/messages/read`, request)
  }
}

/**
 * API for participants in conversations.
 */
export class ParticipantApi extends ApiClient {
  /**
   * Updates a participant.
   */
  update(
    conversationId: string,
    userId: string,
    request: UpdateParticipantRequest,
  ): Promise<ParticipantResource> {
    return this._patch(
      `v3/conversations/${conversationId}/participants/${userId}/update`,
      request,
    )
  }
}

/**
 * API for user status information.
 */
export class UserStatusApi extends ApiClient {
  /**
   * Checks if users are online.
   */
  isOnline(request: ListUserStatusRequest): Promise<OnlineStatusResource[]> {
    return this._post(`v3/users/status/id-query`, request.user_public_ids)
  }
}

export class RoomsApi extends ApiClient {
  /**
   * Gets a Room
   * @param id
   */
  get(id: string): Promise<RoomResource> {
    return this._get(`v3/rooms/${encodeURIComponent(id)}`)
  }

  /**
   * Gets the connection info to join a room
   * @param id
   */
  join(id: string): Promise<RoomConnectionDescriptorResource> {
    return this._get(`v3/rooms/${encodeURIComponent(id)}/join`)
  }
}
